import {
    Card,
    Row,
    Col,
    Form,
    Select,
    DatePicker,
    Button,
    Input,
    Modal,
    Breadcrumb,
    Typography,
    Table,
    Upload,
    Grid,
} from "antd";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../context/AuthContext";
import { NavigationContext } from "../../context/NavigationContext";
import LeaveTable from "./LeaveTable";
import LeaveCalendar from "./LeaveCalendar";
import { UploadOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Title } = Typography;
const lightTheme = "#0e487bdf";

export default function Leaves() {
    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);
    const screens = Grid.useBreakpoint();
    const [leaveDetails, setLeaveDetails] = useState(null);

    const DateOfJoining = authData?.employee?.date_of_joining;

    const [showModal, setShowModal] = useState(false);
    const [targetLeaveId, setTargetLeaveId] = useState(null);
    const [selectedCompoffs, setSelectedCompoffs] = useState([]);
    const [showUpload, setShowUpload] = useState(false);
    const [durationType, setDurationType] = useState("");
    const [employee, setEmployee] = useState(null);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({
        totals: {},
        allotments: {},
        balances: {},
    });
    const [showAll, setShowAll] = useState(false);
    const [isCompoff, setIsCompoff] = useState(false);
    const [selectedLeaveType, setSelectedLeaveType] = useState("");
    const [form] = Form.useForm();

    // Available compoff/weekoff counts for disabling dropdown options
    const [availableCompoffs, setAvailableCompoffs] = useState(0);
    const [availableWeekoffs, setAvailableWeekoffs] = useState(0);

    const currentMonth = new Date().getMonth();

    const fetchEmployee = async () => {
        if (!authData?.employee?.id) return;
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/employees/${authData.employee.id}/`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setEmployee(res.data);
        } catch (err) {
            console.error("Error fetching employee:", err);
        }
    };

    useEffect(() => {
        fetchEmployee();
    }, []);

    const fetchLeaves = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/hr/leave-summary`, {
                headers: { Authorization: `Bearer ${authData.access}` },
            });
            const apiData = res.data.monthly_details || [];
            setData(apiData);
            setSummary({
                totals: res.data.totals || {},
                allotments: res.data.allotments || {},
                balances: res.data.balances || {},
            });
        } catch (error) {
            console.error("Error fetching leave summary:", error);
        }
    };

    const fetchCompoffAvailability = async () => {
        try {
            const [compoffRes, weekoffRes] = await Promise.all([
                axios.get(
                    `${backUrl}/api/hr/compoffs/my-compoffs/?status=Available,PartiallyUsed&leave_type=Compoff`,
                    { headers: { Authorization: `Bearer ${authData.access}` } }
                ),
                axios.get(
                    `${backUrl}/api/hr/compoffs/my-compoffs/?status=Available,PartiallyUsed&leave_type=Weekoff`,
                    { headers: { Authorization: `Bearer ${authData.access}` } }
                ),
            ]);
            setAvailableCompoffs(compoffRes.data.results?.length ?? 0);
            setAvailableWeekoffs(weekoffRes.data.results?.length ?? 0);
        } catch (err) {
            console.error("Error fetching compoff availability:", err);
        }
    };

    useEffect(() => {
        fetchLeaves();
        fetchCompoffAvailability();
    }, []);

    // ✅ Handle navigation from notifications - auto-open modal with specific leave
    useEffect(() => {
        const handleNotificationNavigation = async () => {
            if (navigationParams?.leaveId && navigationParams?.openModal) {
                console.log("🎯 Leaves: Received navigation params:", navigationParams);
                const id = navigationParams.leaveId;
                console.log("📥 Fetching leave with ID:", id);
                
                try {
                    // Fetch the specific leave by ID
                    const res = await axios.get(
                        `${backUrl}/api/hr/leaves/${id}/`,
                        {
                            headers: { Authorization: `Bearer ${authData?.access}` },
                        }
                    );
                    
                    console.log("✅ Leave data fetched:", res.data);
                    
                    // Set the leave data as an array (LeaveTable expects an array)
                    setLeaveDetails([res.data]);
                    // Store the target ID for row highlighting
                    setTargetLeaveId(id);
                    // Open the modal
                    setShowModal(true);
                    
                    console.log("✅ Modal opened with leave details");
                } catch (error) {
                    console.error("❌ Error fetching leave details:", error);
                    // Fallback: open modal with all leaves and let user find it
                    setLeaveDetails(null);
                    setTargetLeaveId(id);
                    setShowModal(true);
                    toast.error("Could not load specific leave. Showing all leaves.");
                } finally {
                    clearNavigationParams();
                }
            }
        };

        handleNotificationNavigation();
    }, [navigationParams, backUrl, authData, clearNavigationParams]);

    const filteredData = showAll ? data : data.filter((_, idx) => idx <= currentMonth);

    const GetCompoff = ({ leaveType }) => {
        const [cData, setCdata] = useState([]);
        const [loading, setLoading] = useState(false);

        const fetchCompoff = async () => {
            try {
                setLoading(true);
                const res = await axios.get(
                    `${backUrl}/api/hr/compoffs/my-compoffs/?status=Available,PartiallyUsed&leave_type=${leaveType}`,
                    {
                        headers: { Authorization: `Bearer ${authData.access}` },
                    }
                );
                setCdata(res.data.results || []);
            } catch (err) {
                console.error("Error fetching compoffs:", err);
            } finally {
                setLoading(false);
            }
        };

        useEffect(() => {
            fetchCompoff();
        }, []);

        return (
            <>
                <label style={{ fontWeight: 500 }}>Select Worked Date</label>
                <Select
                    mode="multiple"
                    loading={loading}
                    placeholder="Select available comp-off date(s)"
                    style={{ width: "100%", marginBottom: "10px" }}
                    onChange={(values) => setSelectedCompoffs(values)}
                    value={selectedCompoffs}
                    allowClear
                >
                    {cData.length > 0 ? (
                        cData.map((item) => (
                            <Select.Option key={item.id} value={item.id}>
                                {dayjs(item.work_date).format("DD/MM/YYYY")}
                            </Select.Option>
                        ))
                    ) : (
                        <Select.Option disabled>No available week-offs</Select.Option>
                    )}
                </Select>
            </>
        );
    };

    const handleDateChange = (type = null) => {
        const leaveType = type || form.getFieldValue("leave_type") || selectedLeaveType;
        const start = form.getFieldValue("start_date");
        const end = form.getFieldValue("end_date");

        if (leaveType === "Sick" && start && end) {
            const diff = dayjs(end).diff(dayjs(start), "day") + 1;
            setShowUpload(diff > 3);
        } else {
            setShowUpload(false);
        }
    };

    const [submitting, setSubmitting] = useState(false);

    const onFinish = async (values) => {
        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append("leave_type", values.leave_type);
            formData.append("duration_type", values.duration_type);
            formData.append("reason", values.reason);

            if (values.session) formData.append("session", values.session);
            if (values.start_date)
                formData.append("start_date", values.start_date);

            if (values.end_date)
                formData.append("end_date", values.end_date);

            if (values.leave_document?.fileList?.length > 0) {
                const fileObj = values.leave_document.fileList[0].originFileObj;
                formData.append("leave_document", fileObj);
            }

            if (selectedCompoffs?.length > 0) {
                selectedCompoffs.forEach((id) =>
                    formData.append("compoff_credits", id)
                );
            }

            await axios.post(`${backUrl}/api/hr/leaves/`, formData, {
                headers: {
                    Authorization: `Bearer ${authData.access}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            toast.success("Leave application submitted successfully!");
            form.resetFields();
            setShowUpload(false);
            setIsCompoff(false);
            setSelectedCompoffs([]);
            setSelectedLeaveType("");
            fetchLeaves();

        } catch (error) {
            const rawError =
                error.response?.data?.data ||
                error.response?.data?.detail ||
                error.response?.data?.message ||
                "Failed to submit leave application.";

            const errorMsg = Array.isArray(rawError)
                ? rawError.join(", ")
                : rawError;

            toast.error(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };


    const columns = [
        {
            title: "Month",
            dataIndex: "month",
            render: (value, record) => {
                const monthName = record.month ?? "-";

                return (
                    <span>
                        {monthName}
                        {record.Lop_Taken > 0 && (
                            <span style={{ color: "red", marginLeft: 8 }}>
                                (LOP: {record.Lop_Taken})
                            </span>
                        )}
                    </span>
                );
            },
        },
        { title: "SL Taken", dataIndex: "SL_Taken" },
        { title: "CL Taken", dataIndex: "CL_Taken" },
        // { title: "EL Taken", dataIndex: "EL_Taken" },
        { title: "Week Off Taken", dataIndex: "WeekOff_Taken" },
        { title: "Comp off Taken", dataIndex: "Compoff_Taken" },
    ];

    return (
        <>
            <ToastContainer />
            <Breadcrumb style={{ marginBottom: 24, cursor: "pointer" }}>
                <Breadcrumb.Item
                    onClick={() => setShowModal(true)}
                    style={{ cursor: "pointer" }}
                >
                    View Leaves
                </Breadcrumb.Item>
            </Breadcrumb>

            {/* Responsive Modal */}
            {showModal && (
                <Modal
                    title=""
                    open={showModal}
                    onCancel={() => {
                        setShowModal(false);
                        setTargetLeaveId(null);
                    }}
                    footer={null}
                    width={screens.xs || screens.sm ? "90%" : 1300}
                    style={{
                        top: screens.xs ? 10 : 50,
                        padding: screens.xs ? "0 5px" : "0 20px",
                    }}
                    bodyStyle={{
                        padding: screens.xs ? "10px 5px" : "20px",
                    }}
                >
                    <LeaveTable data={leaveDetails} targetLeaveId={targetLeaveId} />
                </Modal>
            )}

            <Row
                gutter={[24, 24]}
                justify="center"
                style={{ padding: screens.xs ? "0 8px" : "0" }}
            >
                <Col xs={24} lg={14}>
                    <LeaveCalendar />
                    <br />
                    <br />
                    <br />
                    <br />
                    <br />
                    <br />
                    <br />
                    <br />
                    <br />
                </Col>

                <Col xs={24} lg={10}>
                    <Card
                        bordered
                        style={{
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            borderColor: "#0e487b6d",
                            height: screens.xs ? "auto" : "690px",
                            padding: screens.xs ? 12 : 24,
                        }}
                    >
                        <Title
                            level={screens.xs ? 5 : 4}
                            style={{
                                textAlign: "center",
                                color: "#0e487b",
                                marginBottom: 24,
                            }}
                        >
                            Apply for Leave
                        </Title>

                        <Form layout="vertical" form={form} onFinish={onFinish} autoComplete="off">
                            <Form.Item
                                label="Leave Type"
                                name="leave_type"
                                rules={[{ required: true, message: "Please select leave type" }]}
                            >
                                <Select
                                    placeholder="Select Leave Type"
                                    onChange={(v) => {
                                        setSelectedLeaveType(v);
                                        if (v === "Compoff" || v === "Weekoff") {
                                            setIsCompoff(true);
                                        } else {
                                            setIsCompoff(false);
                                            setSelectedCompoffs([]);
                                        }
                                        handleDateChange(v);
                                    }}
                                >
                                    <Select.Option
                                        value="Sick"
                                        disabled={summary.balances?.SL <= 0}
                                    >
                                        Sick Leave
                                    </Select.Option>
                                    <Select.Option
                                        value="Casual"
                                        disabled={summary.balances?.CL <= 0}
                                    >
                                        Casual Leave
                                    </Select.Option>
                                    {/* <Select.Option
                                        value="Earned"
                                        disabled={
                                            !authData?.employee?.taken_earned_leave ||
                                            authData?.employee?.taken_earned_leave === 0
                                        }
                                    >
                                        Earned Leave
                                    </Select.Option> */}
                                    <Select.Option value="Lop">NLB (Nil Leave Balance)</Select.Option>
                                    <Select.Option
                                        value="Weekoff"
                                        disabled={availableWeekoffs <= 0}
                                    >
                                        Weekoff
                                    </Select.Option>
                                    <Select.Option
                                        value="Compoff"
                                        disabled={availableCompoffs <= 0}
                                    >
                                        Compoff
                                    </Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label="Duration"
                                name="duration_type"
                                rules={[{ required: true, message: "Please select duration" }]}
                            >
                                <Select
                                    placeholder="Select Duration"
                                    onChange={(value) => setDurationType(value)}
                                >
                                    <Select.Option value="Full Day">Full Day</Select.Option>
                                    <Select.Option value="Half Day">Half Day</Select.Option>
                                </Select>
                            </Form.Item>

                            {durationType === "Half Day" && (
                                <Form.Item
                                    label="Session"
                                    name="session"
                                    rules={[{ required: true, message: "Please select session" }]}
                                >
                                    <Select placeholder="Select Session">
                                        <Select.Option value="FN">Forenoon ( 9:30am to 1:30pm )</Select.Option>
                                        <Select.Option value="AN">Afternoon ( 2:00pm to 6:00pm )</Select.Option>
                                    </Select>
                                </Form.Item>
                            )}

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="Start Date"
                                        name="start_date"
                                        rules={[{ required: true, message: "Please select start date" }]}
                                    >
                                        <input
                                            type="date"
                                            style={{ width: "100%" }}
                                            min={authData?.employee?.date_of_joining}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (!value) return;

                                                form.setFieldsValue({ start_date: value });
                                                handleDateChange();
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label="End Date"
                                        name="end_date"
                                        rules={[{ required: true, message: "Please select end date" }]}
                                    >
                                        <input
                                            type="date"
                                            style={{ width: "100%" }}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (!value) return;

                                                form.setFieldsValue({ end_date: value });
                                                handleDateChange();
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                label="Reason"
                                name="reason"
                                rules={[{ required: true, message: "Please enter reason" }]}
                            >
                                <TextArea rows={3} placeholder="Enter reason for leave" />
                            </Form.Item>

                            {/* Only show GetCompoff when Compoff or Weekoff is selected */}
                            {isCompoff && <GetCompoff leaveType={selectedLeaveType} />}

                            {selectedLeaveType === "Sick" && showUpload && (
                                <Form.Item
                                    label="Attachment *"
                                    name="leave_document"
                                    valuePropName="file"
                                    getValueFromEvent={(e) => e}
                                    rules={[{ required: true, message: "Please upload supporting document" }]}
                                >
                                    <Upload beforeUpload={() => false} maxCount={1}>
                                        <Button icon={<UploadOutlined />}>Click to Upload File</Button>
                                    </Upload>
                                </Form.Item>
                            )}

                            <Form.Item style={{ textAlign: "center", marginTop: 30 }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={submitting}
                                    disabled={submitting}
                                    style={{
                                        backgroundColor: "#0e487b",
                                        width: screens.xs ? "100%" : "auto",
                                    }}
                                >
                                    {submitting ? "Submitting..." : "Submit Leave Application"}
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
            </Row>

            <br />
            <br />
            <br />
            <br />

            <section style={{ marginTop: 32, overflowX: screens.xs ? "auto" : "visible" }}>
                <div style={{ marginBottom: 16, textAlign: "right" }}>
                    <Button
                        type="link"
                        onClick={() => setShowAll(!showAll)}
                        style={{ color: "#0e497b" }}
                    >
                        {showAll ? "Show Jan → Current Month" : "Show All Months"}
                    </Button>
                </div>

                <Table
                    columns={columns.map((col) => {
                        if (col.dataIndex === "month") {
                            return {
                                ...col,
                                render: (value, record) => {
                                    const monthName =
                                        typeof record.month === "object"
                                            ? record.month.month ?? "-"
                                            : record.month ?? "-";
                                    return (
                                        <span>
                                            {monthName}{" "}
                                            {/* {record.Lop_Taken > 0 && (
                                                <span style={{ color: "red" }}>
                                                    {record.Lop_Taken} LOP applied
                                                </span>
                                            )} */}
                                        </span>
                                    );
                                },
                            };
                        }

                        return {
                            ...col,
                            render: (value, record) => {
                                const displayValue =
                                    typeof value === "object" ? JSON.stringify(value) : value ?? 0;

                                return (
                                    <a
                                        href="#"
                                        onClick={async (e) => {
                                            e.preventDefault();

                                            const monthNum =
                                                typeof record.month === "object"
                                                    ? record.month.number ?? ""
                                                    : new Date(record.month + " 1, 2025").getMonth() + 1;

                                            let leaveType = col.title.replace(/\s+/g, "");
                                            if (leaveType === "SLTaken") leaveType = "Sick";
                                            else if (leaveType === "CLTaken") leaveType = "Casual";
                                            else if (leaveType === "ELTaken") leaveType = "Earned";
                                            else leaveType = "Compoff";

                                            const apiUrl = `${backUrl}/api/hr/leaves/my-leaves/?month=${monthNum}&leave_type=${leaveType}`;
                                            console.log("API URL:", apiUrl);

                                            try {
                                                const res = await axios.get(apiUrl, {
                                                    headers: { Authorization: `Bearer ${authData?.access}` },
                                                });

                                                console.log("Month wise Leave Data:", res.data);
                                                setLeaveDetails(res.data.results);
                                                setShowModal(true);
                                            } catch (error) {
                                                console.error("Error fetching leave data:", error);
                                            }
                                        }}
                                        style={{
                                            textDecoration: "none",
                                            cursor: "pointer",
                                            color: "#0e497b",
                                        }}
                                    >
                                        {displayValue}
                                    </a>
                                );
                            },
                        };
                    })}
                    dataSource={filteredData}
                    pagination={false}
                    bordered
                    rowKey={(record) => record.month}
                    size={screens.xs ? "small" : "middle"}
                    scroll={screens.xs ? { x: 600 } : undefined}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                        fontSize: screens.xs ? "0.75rem" : "1rem",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    summary={() => (
                        <>
                            {/* ---- TOTAL ROW ---- */}
                            <Table.Summary.Row
                                onClick={() => alert("Total row clicked")}
                                style={{
                                    backgroundColor: lightTheme,
                                    fontWeight: "bold",
                                    color: "white",
                                    cursor: "pointer",
                                }}
                            >
                                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>
                                    {summary.totals.SL_Taken ?? 0}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>
                                    {summary.totals.CL_Taken ?? 0}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3}>
                                    {summary.totals.WeekOff_Taken ?? 0}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4}>
                                    {summary.totals.Compoff_Taken ?? 0}
                                </Table.Summary.Cell>
                            </Table.Summary.Row>

                            {/* ---- ALLOTMENT ROW ---- */}
                            <Table.Summary.Row
                                onClick={() => alert("Allotment row clicked")}
                                style={{
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                }}
                            >
                                <Table.Summary.Cell index={0}>Allotment</Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>
                                    {summary.allotments.SL ?? 0}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>
                                    {summary.allotments.CL ?? 0}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} colSpan={2} style={{ textAlign: "center" }}>
                                    {(summary.allotments.WeekOff ?? 0) + (summary.allotments.Compoff ?? 0)}
                                </Table.Summary.Cell>
                            </Table.Summary.Row>

                            {/* ---- BALANCE ROW ---- */}
                            <Table.Summary.Row
                                onClick={() => alert("Balance row clicked")}
                                style={{
                                    backgroundColor: lightTheme,
                                    fontWeight: "bold",
                                    color: "white",
                                    cursor: "pointer",
                                }}
                            >
                                <Table.Summary.Cell index={0}>Balance</Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>
                                    {summary.balances.SL ?? 0}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>
                                    {summary.balances.CL ?? 0}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3}>
                                    {summary.balances.WeekOff ?? 0}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4}>
                                    {summary.balances.Compoff ?? 0}
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </>
                    )}
                />
            </section>
        </>
    );
}